import React from 'react';
import { Plus, Trash2, FileText, Sparkles, Newspaper, EyeOff } from 'lucide-react';
import { GlassButton } from '../ui';
import { ImportedPage, HeadMetaEntry } from '../../services/api';

interface Props {
  bundlePages: string[];          // pages that exist as files — read-only, always win when served
  generatedPages: ImportedPage[]; // pages AIME synthesises from a bundle page's chrome
  currentPage: string;            // page_rel of the page in the iframe
  headMeta?: Record<string, HeadMetaEntry>; // bundle host page noindex/meta, keyed by page_rel
  onSelect: (pageRel: string) => void;
  onNewPage: () => void;
  onDeletePage: (page: ImportedPage) => void;
  onAddSection: () => void;
  entrypoint: string;
  /** Rendered at the end of the scrolling list (the editor's "Sections on this page"). */
  children?: React.ReactNode;
}

const NoindexBadge: React.FC = () => (
  <span title="Hidden from search engines (noindex)"
        style={{ display: 'inline-flex', alignItems: 'center', flexShrink: 0, color: 'var(--text-secondary)' }}>
    <EyeOff size={11} />
  </span>
);

const rowBase: React.CSSProperties = {
  display: 'flex', alignItems: 'center', gap: 8, width: '100%', textAlign: 'left',
  padding: '7px 12px', border: 'none', cursor: 'pointer', font: 'inherit', fontSize: 12,
};
const groupLabel: React.CSSProperties = {
  fontSize: 10, fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase',
  color: 'var(--text-secondary)', padding: '10px 12px 4px',
};

/**
 * Left rail of the imported-site editor: the bundle's own pages (read-only) and the pages AIME
 * generates on top of them.
 *
 * The two groups are shown separately because they behave differently and the difference is not
 * cosmetic: a bundle file is served verbatim and always wins over a generated page at the same URL,
 * and only generated pages can be renamed or deleted here.
 *
 * Inline styles + CSS variables, mirroring ImportedSiteEditor's right-hand properties rail (that
 * file has no stylesheet). Never hardcode colours.
 */
const PageListPanel: React.FC<Props> = ({
  bundlePages, generatedPages, currentPage, headMeta, onSelect, onNewPage, onDeletePage, onAddSection,
  entrypoint, children,
}) => {
  const isGenerated = generatedPages.some(p => p.page_rel === currentPage);

  return (
    <div style={{ width: 240, borderRight: '1px solid var(--stroke)', overflowY: 'auto',
                  background: 'var(--panel)', display: 'flex', flexDirection: 'column' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                    padding: '8px 12px', borderBottom: '1px solid var(--stroke)' }}>
        <span style={{ fontSize: 12, fontWeight: 600, color: 'var(--text)' }}>Pages</span>
        <button onClick={onNewPage} title="New page"
                style={{ border: 'none', background: 'none', cursor: 'pointer',
                         color: 'var(--primary-500)', padding: 2, display: 'flex' }}>
          <Plus size={16} />
        </button>
      </div>

      <div style={{ flex: 1 }}>
        <div style={groupLabel}>From your upload</div>
        {bundlePages.map(p => (
          <button key={p} onClick={() => onSelect(p)}
                  style={{ ...rowBase,
                           background: currentPage === p ? 'rgba(33,119,255,0.08)' : 'transparent',
                           color: currentPage === p ? 'var(--text)' : 'var(--text-secondary)',
                           fontWeight: currentPage === p ? 600 : 400 }}>
            <FileText size={14} style={{ flexShrink: 0 }} />
            <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {p}
            </span>
            {headMeta?.[p]?.noindex && <NoindexBadge />}
            {p === entrypoint && (
              <span style={{ fontSize: 10, color: 'var(--text-secondary)' }}>home</span>
            )}
          </button>
        ))}

        <div style={groupLabel}>Added by AiMe</div>
        {generatedPages.length === 0 && (
          <div style={{ padding: '4px 12px 10px', fontSize: 11, color: 'var(--text-secondary)' }}>
            None yet. New pages copy your site’s look automatically.
          </div>
        )}
        {generatedPages.map(p => (
          <div key={p.page_id}
               style={{ display: 'flex', alignItems: 'center',
                        background: currentPage === p.page_rel ? 'rgba(33,119,255,0.08)' : 'transparent' }}>
            <button onClick={() => onSelect(p.page_rel)}
                    style={{ ...rowBase, background: 'transparent', flex: 1, minWidth: 0,
                             color: currentPage === p.page_rel ? 'var(--text)' : 'var(--text-secondary)',
                             fontWeight: currentPage === p.page_rel ? 600 : 400 }}>
              {p.kind === 'blog_index'
                ? <Newspaper size={14} style={{ flexShrink: 0 }} />
                : <Sparkles size={14} style={{ flexShrink: 0 }} />}
              <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {p.title}
              </span>
              {p.noindex && <NoindexBadge />}
            </button>
            <button onClick={() => onDeletePage(p)} title={`Delete ${p.title}`}
                    style={{ border: 'none', background: 'none', cursor: 'pointer',
                             color: 'var(--text-secondary)', padding: '0 8px', display: 'flex' }}>
              <Trash2 size={13} />
            </button>
          </div>
        ))}

        {children && (
          <div style={{ borderTop: '1px solid var(--stroke)', marginTop: 8 }}>
            {children}
          </div>
        )}
      </div>

      {isGenerated && (
        <div style={{ padding: 10, borderTop: '1px solid var(--stroke)' }}>
          {/* Generated pages get no "+ Add section here" hover zones — there is no host content to
              anchor between, so sections are appended in order. This is the entry point instead. */}
          <GlassButton variant="secondary" onClick={onAddSection} style={{ width: '100%' }}>
            <Plus size={14} /> Add section
          </GlassButton>
        </div>
      )}
    </div>
  );
};

export default PageListPanel;
